/**
 * React Context объекты для действий приложения.
 *
 * Вынесены в отдельный файл чтобы Fast Refresh (HMR) работал корректно:
 * файл содержит только не-компоненты (Context объекты), что удовлетворяет
 * правилу react-refresh/only-export-components.
 *
 * Хуки-потребители — в ./useAppActions.ts и ./useNoWishlistDrawer.ts.
 * Сам Provider — в ./ActionsProvider.tsx.
 */

import { createContext } from 'react';
import type { ItemPrefillData } from '@/lib';
import type { BottomMenuItemId } from '@/config';
import type { AppActionsContextValue } from './model/types';

/** No-op заглушка для callback'ов в fallback значениях */
const NOOP = () => {};

/** Fallback значение когда ActionsProvider не найден выше по дереву */
export const ACTIONS_FALLBACK_VALUE: AppActionsContextValue = {
  wishlist: { createFromMenu: NOOP },
  item: { createFromMenu: NOOP, addPrefilled: NOOP },
  quickMenu: { findFriend: NOOP, secretSanta: NOOP },
  navigation: { handleMenuItemChange: NOOP as (id: BottomMenuItemId) => void },
};

/** Объединённый контекст всех действий приложения */
export const AppActionsContext = createContext<AppActionsContextValue | null>(null);

/** Значение контекста drawer'а "нет вишлиста" */
export interface NoWishlistDrawerContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  pendingPrefill: ItemPrefillData | null;
  clearPendingPrefill: () => void;
}

/** Контекст для управления drawer "нет вишлиста" */
export const NoWishlistDrawerContext = createContext<NoWishlistDrawerContextValue>({
  open: false,
  setOpen: NOOP,
  pendingPrefill: null,
  clearPendingPrefill: NOOP,
});
